import { useState, useCallback, useEffect, useRef } from 'react';

export interface UseCustomScoresModalReturn {
  // Modal state
  isCustomScoresModalOpen: boolean;
  modalSessionId: string | null;
  modalAnchorRect: DOMRect | null;
  isHoveringModalArea: boolean;
  
  // Modal actions
  showModal: (sessionId: string, rect: DOMRect) => void;
  hideModal: () => void;
  cancelHideModal: () => void;
  startHideModal: () => void;
  closeModal: () => void;
}

/**
 * Custom hook to manage custom scores modal state and hover interactions.
 * Provides robust hover logic that prevents modal from closing when cursor is inside modal area.
 */
export function useCustomScoresModal(): UseCustomScoresModalReturn {
  const [isCustomScoresModalOpen, setIsCustomScoresModalOpen] = useState(false);
  const [modalSessionId, setModalSessionId] = useState<string | null>(null);
  const [modalAnchorRect, setModalAnchorRect] = useState<DOMRect | null>(null);
  const [isHoveringModalArea, setIsHoveringModalArea] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        global.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Clear timeout helper
  const clearHideTimeout = useCallback(() => {
    if (timeoutRef.current) {
      global.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const showModal = useCallback((sessionId: string, rect: DOMRect) => {
    clearHideTimeout();
    setModalSessionId(sessionId);
    setModalAnchorRect(rect);
    setIsCustomScoresModalOpen(true);
    setIsHoveringModalArea(false);
  }, [clearHideTimeout]);

  const hideModal = useCallback(() => {
    clearHideTimeout();
    const timeoutId = global.setTimeout(() => {
      setIsCustomScoresModalOpen(false);
      setModalSessionId(null);
      setModalAnchorRect(null);
      setIsHoveringModalArea(false);
    }, 150);
    timeoutRef.current = timeoutId;
  }, [clearHideTimeout]);

  const cancelHideModal = useCallback(() => {
    clearHideTimeout();
    setIsHoveringModalArea(true);
  }, [clearHideTimeout]);

  const startHideModal = useCallback(() => {
    clearHideTimeout();
    setIsHoveringModalArea(false);
    const timeoutId = global.setTimeout(() => {
      setIsCustomScoresModalOpen(false);
      setModalSessionId(null);
      setModalAnchorRect(null);
      setIsHoveringModalArea(false);
    }, 150);
    timeoutRef.current = timeoutId;
  }, [clearHideTimeout]);

  const closeModal = useCallback(() => {
    clearHideTimeout();
    setIsCustomScoresModalOpen(false);
    setModalSessionId(null);
    setModalAnchorRect(null);
    setIsHoveringModalArea(false);
  }, [clearHideTimeout]);

  return {
    // State
    isCustomScoresModalOpen,
    modalSessionId,
    modalAnchorRect,
    isHoveringModalArea,
    
    // Actions
    showModal,
    hideModal,
    cancelHideModal,
    startHideModal,
    closeModal,
  };
}